import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, IDR, imgUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import WaitlistForm from "@/components/WaitlistForm";
const PLACE = "https://images.unsplash.com/photo-1503341504253-dff4815485f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxibGFjayUyMHQtc2hpcnQlMjBzdHJlZXR3ZWFyJTIwbW9kZWx8ZW58MHx8fHwxNzg4MjQyNDEwfDA&ixlib=rb-4.1.0&q=85";

function SizeChartModal({ open, onClose }) {
  const [chart, setChart] = useState(null);
  useEffect(() => { if (open && !chart) api.get("/size-chart/tshirt").then((r) => setChart(r.data)); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[color:var(--bg)] border border-white max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] tracking-widest text-neutral-500">SIZE CHART</div>
            <h2 className="display text-2xl">{chart?.title || "T-SHIRT"}</h2>
          </div>
          <button data-testid="size-chart-close" onClick={onClose} className="text-neutral-400 hover:text-white text-2xl leading-none">×</button>
        </div>
        {!chart ? <div className="text-neutral-500 text-sm">Memuat...</div> : (
          <>
            <table className="w-full text-sm mb-3">
              <thead className="text-[10px] tracking-widest text-neutral-500">
                <tr className="border-b border-[color:var(--border)]">
                  <th className="p-2 text-left">SIZE</th>
                  <th className="p-2 text-right">LENGTH (cm)</th>
                  <th className="p-2 text-right">WIDTH (cm)</th>
                </tr>
              </thead>
              <tbody>
                {chart.sizes.map((s) => (
                  <tr key={s.size} className="border-b border-[color:var(--border)]">
                    <td className="p-2 mono">{s.size}</td>
                    <td className="p-2 tabular-nums text-right">{s.length_cm}</td>
                    <td className="p-2 tabular-nums text-right">{s.width_cm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[11px] text-neutral-400">Tolerance ±{chart.tolerance_cm} cm.</div>
            <div className="text-[11px] text-neutral-500 mt-2">{chart.material}</div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [showChart, setShowChart] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setColor(r.data.colors?.[0] || "");
    }).catch(() => nav("/shop"));
  }, [slug]);

  useEffect(() => { setActive(0); }, [color]);

  if (!p) return <div className="p-8 text-neutral-500">Memuat...</div>;

  const legacyImages = [p.front_image, p.back_image, ...(p.gallery || [])].filter(Boolean);
  const colorSlot = color && (p.color_images || {})[color];
  const colorImages = colorSlot
    ? [colorSlot.front, colorSlot.back, ...(colorSlot.gallery || [])].filter(Boolean)
    : [];
  // Rule #5: fallback to default/existing images if this color has none.
  const images = colorImages.length ? colorImages : legacyImages;
  const displayImages = images.length ? images.map(imgUrl) : [PLACE];
  const soldOut = p.status === "SOLD_OUT";
  const soon = p.status === "COMING_SOON";
  const canBuy = p.status === "AVAILABLE";

  const doAdd = (buyNow = false) => {
    if (!size) return toast.error("Pilih ukuran terlebih dahulu");
    if (!color) return toast.error("Pilih warna terlebih dahulu");
    const cartImg = (colorSlot && (colorSlot.front || colorSlot.back || (colorSlot.gallery || [])[0])) || p.front_image;
    add({ product_id: p.id, article_code: p.article_code, name: p.name, price: p.price, size, color, qty, image: cartImg });
    toast.success("Ditambahkan ke keranjang");
    if (buyNow) nav("/cart");
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-8">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
        {/* Images */}
        <div>
          <div className="aspect-[4/5] bg-[color:var(--surface)] overflow-hidden mb-2">
            <img src={displayImages[active]} alt={p.name} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            {displayImages.map((im, i) => (
              <button key={i} data-testid={`thumb-${i}`} onClick={() => setActive(i)} className={`aspect-square border ${i === active ? "border-white" : "border-[color:var(--border)]"}`}>
                <img src={im} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="lg:sticky lg:top-24 self-start">
          <div className="text-[10px] tracking-widest text-neutral-500">{p.category_name} · {p.article_code}</div>
          <h1 className="display text-5xl md:text-7xl leading-none mt-2">{p.name}</h1>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            {p.sale_price ? (
              <>
                <div className="text-2xl tabular-nums text-[color:var(--accent)]">{IDR(p.sale_price)}</div>
                <div className="text-sm text-neutral-500 line-through">{IDR(p.price)}</div>
              </>
            ) : (
              <div className="text-2xl tabular-nums">{IDR(p.price)}</div>
            )}
            <span data-testid="prod-status" className={`status-badge ${soldOut ? "bg-[color:var(--accent)] text-white border-transparent" : ""}`}>
              {soldOut ? "SOLD OUT" : soon ? "COMING SOON" : "AVAILABLE"}
            </span>
            <span className="status-badge">{(p.production_type || "").replace(/_/g," ")}</span>
            <span className="status-badge">CETAK {p.production_method?.replace(/_/g," ")}</span>
          </div>

          <p className="text-neutral-300 text-sm mt-6 leading-relaxed whitespace-pre-line">{p.description}</p>

          <div className="mt-8">
            <div className="text-[10px] tracking-widest text-neutral-500 mb-2">WARNA</div>
            <div className="flex gap-2">
              {p.colors?.map((c) => (
                <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)}
                  className={`hairline px-4 py-2 text-xs ${color === c ? "bg-white text-black" : ""}`}>{c}</button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="text-[10px] tracking-widest text-neutral-500 mb-2 flex justify-between">
              <span>UKURAN</span>
              <button type="button" data-testid="size-chart-btn" onClick={() => setShowChart(true)} className="underline">SIZE GUIDE</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {p.sizes?.map((s) => {
                const stock = p.production_type === "READY_STOCK" ? (p.size_stock || {})[s] || 0 : null;
                const disabled = p.production_type === "READY_STOCK" && stock <= 0;
                return (
                  <button key={s} type="button" data-testid={`size-${s}`} disabled={disabled} onClick={() => setSize(s)}
                    className={`hairline w-14 h-12 text-xs relative ${size === s ? "bg-white text-black" : ""} ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}>
                    {s}
                    {stock !== null && stock > 0 && stock <= 3 && <span className="absolute -top-1 -right-1 text-[9px] bg-[color:var(--accent)] text-white px-1">{stock}</span>}
                  </button>
                );
              })}
            </div>
            {p.production_type === "MADE_TO_ORDER" && (
              <div className="text-[11px] text-neutral-500 mt-2">MADE TO ORDER — diproduksi setelah kamu bayar. Estimasi produksi 7–14 hari kerja sebelum dikirim dari Majalengka.</div>
            )}
          </div>

          <div className="mt-6">
            <div className="text-[10px] tracking-widest text-neutral-500 mb-2">JUMLAH</div>
            <div className="inline-flex hairline">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10">−</button>
              <span data-testid="qty" className="w-12 h-10 flex items-center justify-center border-x border-[color:var(--border)]">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(Math.min(20, qty + 1))} className="w-10 h-10">+</button>
            </div>
          </div>

          {canBuy ? (
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button data-testid="add-to-cart-btn" onClick={() => doAdd(false)} className="flex-1 bg-white text-black py-4 text-xs tracking-widest hover:bg-neutral-200">TAMBAH KE KERANJANG</button>
              <button data-testid="buy-now-btn" onClick={() => doAdd(true)} className="flex-1 border border-white py-4 text-xs tracking-widest hover:bg-white hover:text-black">BELI SEKARANG</button>
            </div>
          ) : soon ? (
            <WaitlistForm productId={p.id} />
          ) : (
            <div className="mt-8 border border-[color:var(--border)] p-4 text-neutral-400 text-sm">
              Produk ini saat ini tidak tersedia.
            </div>
          )}
        </div>
      </div>
      <SizeChartModal open={showChart} onClose={() => setShowChart(false)} />
    </div>
  );
}
